import React, { useState } from 'react';
import { FileText, Hash, Loader2, Upload } from 'lucide-react';
import type { NotaFiscal } from '../../../shared/types';
import { TextInput } from '../../../shared/components/TextInput';
import { useEnviarNotaFiscal } from '../hooks';
import { apiErrorMessage, formatCompetencia, formatCurrency } from '../constants';
import { NFModalShell, btnPrimary, btnSecondary } from './NFModalShell';

const MAX_BYTES = 10 * 1024 * 1024;

interface Props {
  nota: NotaFiscal;
  onClose: () => void;
  onSent: (message: string) => void;
}

/**
 * Envio (nota liberada) ou reenvio (nota rejeitada). Competência e valor
 * vêm da liberação e são somente leitura: o colaborador informa só o
 * número e o PDF.
 */
export const NFSubmitModal: React.FC<Props> = ({ nota, onClose, onSent }) => {
  const enviar = useEnviarNotaFiscal();
  const reenvio = nota.status === 'rejeitada';
  const [number, setNumber] = useState(reenvio ? '' : nota.number ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<{ number?: string; file?: string; api?: string }>({});

  function escolherArquivo(f: File | null) {
    if (f && f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      setErrors((e) => ({ ...e, file: 'Envie um arquivo PDF' }));
      return;
    }
    if (f && f.size > MAX_BYTES) {
      setErrors((e) => ({ ...e, file: 'O PDF deve ter até 10 MB' }));
      return;
    }
    setErrors((e) => ({ ...e, file: undefined }));
    setFile(f);
  }

  async function handleSubmit() {
    const next: typeof errors = {};
    if (!number.trim()) next.number = 'Informe o número da nota fiscal';
    if (!file) next.file = 'Anexe o PDF da nota fiscal';
    setErrors(next);
    if (next.number || next.file) return;
    try {
      await enviar.mutateAsync({ id: nota.id, number: number.trim(), file: file! });
      onSent(`NF de ${formatCompetencia(nota.referenceMonth)} enviada para análise.`);
      onClose();
    } catch (err) {
      setErrors({ api: apiErrorMessage(err, 'Não foi possível enviar a nota fiscal.') });
    }
  }

  return (
    <NFModalShell
      title={reenvio ? 'Reenviar nota fiscal' : 'Enviar nota fiscal'}
      subtitle={`Competência ${formatCompetencia(nota.referenceMonth)}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={btnSecondary} disabled={enviar.isPending}>
            Cancelar
          </button>
          <button type="button" onClick={handleSubmit} className={btnPrimary} disabled={enviar.isPending}>
            {enviar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Enviar
          </button>
        </>
      }
    >
      {reenvio && nota.rejectionReason && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
          <span className="font-medium">Motivo da rejeição:</span> {nota.rejectionReason}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-3 text-sm">
        <div>
          <p className="text-xs text-gray-500">Competência</p>
          <p className="font-medium text-gray-900">{formatCompetencia(nota.referenceMonth)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Valor da nota (liberado)</p>
          <p className="font-medium text-gray-900">{formatCurrency(nota.value)}</p>
        </div>
      </div>
      <p className="text-xs text-gray-500">
        Emita a nota exatamente com o valor liberado. Ele não pode ser alterado aqui.
      </p>

      <TextInput
        label="Número da NF *"
        icon={<Hash className="h-4 w-4" />}
        value={number}
        maxLength={60}
        onChange={(e) => setNumber(e.target.value)}
        error={errors.number}
      />

      <div>
        <label
          className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed px-4 py-4 text-sm ${
            errors.file ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/40'
          }`}
        >
          <FileText className="h-6 w-6 shrink-0 text-gray-400" />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium text-gray-800">{file ? file.name : 'Selecionar PDF da nota *'}</span>
            <span className="text-xs text-gray-500">PDF de até 10 MB</span>
          </span>
          <input
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only"
            onChange={(e) => escolherArquivo(e.target.files?.[0] ?? null)}
          />
        </label>
        {errors.file && <p className="mt-1 text-xs text-red-600">{errors.file}</p>}
      </div>

      {errors.api && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{errors.api}</div>}
    </NFModalShell>
  );
};
